import type { DisplayDicomSeries } from '../../services/dicomSeriesAdapters';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';

export interface ReferenceSeriesStudyGroup {
  id: string;
  label: string;
  studyDate: string | null;
  studyDescription: string | null;
  modalitySummary: string;
  highMatchModalities: Array<'T1w' | 'T2w'>;
  series: DisplayDicomSeries[];
}

const CLASSIFICATION_TIE_BREAKER: Record<DisplayDicomSeries['modality'], number> = {
  T1w: 0,
  T2w: 1,
  FLAIR: 2,
  SWI: 3,
  DWI: 4,
  MRA: 5,
  CT: 6,
  Unknown: 7,
};

export const isHighMatchSeries = (
  series: DisplayDicomSeries
): series is DisplayDicomSeries & { modality: 'T1w' | 'T2w' } =>
  series.matchLevel === 'High match' && (series.modality === 'T1w' || series.modality === 'T2w');

const getStudySeriesRank = (series: DisplayDicomSeries) => {
  if (isHighMatchSeries(series)) return series.modality === 'T1w' ? 0 : 1;
  return series.modality === 'Unknown' ? 3 : 2;
};

export const sortReferenceSeriesWithinStudy = (
  seriesList: DisplayDicomSeries[]
): DisplayDicomSeries[] =>
  [...seriesList].sort((left, right) => {
    const rankDifference = getStudySeriesRank(left) - getStudySeriesRank(right);
    if (rankDifference !== 0) return rankDifference;

    const confidenceDifference = Number(isHighMatchSeries(right)) - Number(isHighMatchSeries(left));
    if (confidenceDifference !== 0) return confidenceDifference;

    const classificationDifference =
      CLASSIFICATION_TIE_BREAKER[left.modality] - CLASSIFICATION_TIE_BREAKER[right.modality];
    if (classificationDifference !== 0) return classificationDifference;

    const seriesNumberDifference =
      (left.seriesNumber ?? Number.MAX_SAFE_INTEGER) -
      (right.seriesNumber ?? Number.MAX_SAFE_INTEGER);
    if (seriesNumberDifference !== 0) return seriesNumberDifference;

    const labelDifference = left.label.localeCompare(right.label);
    return labelDifference !== 0 ? labelDifference : left.id.localeCompare(right.id);
  });

const compareStudyGroups = (left: ReferenceSeriesStudyGroup, right: ReferenceSeriesStudyGroup) => {
  if (left.studyDate !== right.studyDate) {
    if (!left.studyDate) return 1;
    if (!right.studyDate) return -1;
    return right.studyDate.localeCompare(left.studyDate);
  }

  const labelDifference = left.label.localeCompare(right.label);
  return labelDifference !== 0 ? labelDifference : left.id.localeCompare(right.id);
};

export const groupReferenceSeriesByStudy = (
  seriesList: DisplayDicomSeries[]
): ReferenceSeriesStudyGroup[] => {
  const groupedSeries = new Map<string, DisplayDicomSeries[]>();

  seriesList.forEach(series => {
    const studySeries = groupedSeries.get(series.studyInstanceUid) ?? [];
    studySeries.push(series);
    groupedSeries.set(series.studyInstanceUid, studySeries);
  });

  return Array.from(groupedSeries.entries())
    .map(([studyInstanceUid, studySeries]) => {
      const representative = studySeries[0];
      const studyDate = representative.studyDate ?? null;
      const studyDescription = representative.studyDescription ?? null;
      const modalitySummary = Array.from(new Set(studySeries.map(series => series.type))).join(
        ', '
      );
      const highMatchModalities = Array.from(
        new Set(studySeries.filter(isHighMatchSeries).map(series => series.modality))
      ).sort(modality => (modality === 'T1w' ? -1 : 1));
      const labelParts = [
        formatDateDDMMYYYY(studyDate, 'Unknown date'),
        studyDescription || 'Unnamed study',
      ];

      return {
        id: studyInstanceUid,
        label: labelParts.join(' · '),
        studyDate,
        studyDescription,
        modalitySummary: modalitySummary || 'Unknown modality',
        highMatchModalities,
        series: sortReferenceSeriesWithinStudy(studySeries),
      };
    })
    .sort(compareStudyGroups);
};

const getDefaultStudyScore = (group: ReferenceSeriesStudyGroup) =>
  group.highMatchModalities.reduce((score, modality) => score + (modality === 'T1w' ? 2 : 1), 0);

export const getDefaultOpenStudyId = (groups: ReferenceSeriesStudyGroup[]) => {
  if (groups.length === 0) return null;
  if (groups.length === 1) return groups[0].id;

  return groups.reduce((bestGroup, candidate) =>
    getDefaultStudyScore(candidate) > getDefaultStudyScore(bestGroup) ? candidate : bestGroup
  ).id;
};
